import * as axios from "axios";
import apiConfig from "../config/apiConfig";
import constants from "../library/constants";

export default class ApiChat {
    constructor() {
        this.api_token = null;
        this.client = null;
        this.api_url = apiConfig.apiBaseUrl.claudeApi;
        this.api_key = apiConfig.apiBaseUrl.claudeApiID;
        this.useKendraAPI = apiConfig.features?.useKendraAPI || false;
        this.api_ws_url = apiConfig.apiBaseUrl.kendraWebSocket;

        // AgentCore front door. When useHttpStreaming is true, MessagesCreate
        // routes here instead of opening a WebSocket.
        this.useHttpStreaming = apiConfig.features?.useHttpStreaming || false;

        // EMPTY IS THE CORRECT VALUE when the SPA and /api/* are served from
        // the same CloudFront distribution, which is how the PoC is built. An
        // empty base makes the fetch below a relative, same-origin request:
        // no CORS, no preflight, and the cookie is first-party so
        // SameSite=Strict still sends it.
        //
        // Only set agentApi to an absolute origin if the API genuinely lives
        // somewhere else. Hardcoding the app's own origin here would break the
        // moment a custom domain is put in front of the distribution.
        this.agent_api_url = (apiConfig.apiBaseUrl.agentApi || '').replace(/\/$/, '');

        // Handles for the in-flight request, so the UI can offer a "stop
        // generating" button. Exactly one of these is ever set at a time,
        // depending on which transport MessagesCreate picked.
        this.activeAbort = null;    // SSE path
        this.activeSocket = null;   // WebSocket path
    }

    /**
     * Cancel the request that is currently streaming, if there is one.
     *
     * Both transports already resolve with whatever text has accumulated when
     * they are interrupted — the SSE path via its AbortError branch, the
     * WebSocket path via onclose with isComplete still false. So stopping
     * keeps the partial answer instead of throwing it away, which is what
     * Claude and ChatGPT do when you hit stop.
     *
     * Returns true if something was actually cancelled.
     */
    cancelActiveRequest = () => {
        let cancelled = false;

        if (this.activeAbort) {
            this.activeAbort.abort();
            this.activeAbort = null;
            cancelled = true;
        }

        if (this.activeSocket) {
            const socket = this.activeSocket;
            this.activeSocket = null;
            if (socket.readyState === WebSocket.CONNECTING || socket.readyState === WebSocket.OPEN) {
                socket.close();
                cancelled = true;
            }
        }

        return cancelled;
    }

    /**
     * Access token for the front door, when one is reachable.
     *
     * The front door accepts either the BFF session cookie or a bearer token.
     * The cookie only exists once OAuth callbacks are pointed at
     * /api/auth/callback so the code exchange happens server-side. Until then
     * the browser is signed in via oidc-client-ts, which keeps the token in
     * sessionStorage — so read it and send it as a bearer, otherwise there is
     * no credential on the request at all and the API answers 401.
     *
     * Returns null once BFF is in place and nothing is in sessionStorage,
     * which is the desired end state: the cookie is httpOnly and invisible
     * here, and that is the entire point of moving to it.
     */
    getBearerToken = () => {
        // Front-door sign-in (features.useBffSession): the browser holds no
        // token at all, and the httpOnly cookie is the only credential. This
        // also stops a token left in sessionStorage by an earlier
        // oidc-client-ts sign-in from being sent in its place.
        if (apiConfig.features?.useBffSession) return null;
        try {
            const { authority, clientId } = apiConfig.cognito || {};
            if (!authority || !clientId) return null;
            // oidc-client-ts default storage key.
            const raw = sessionStorage.getItem(`oidc.user:${authority}:${clientId}`);
            if (!raw) return null;
            return JSON.parse(raw).access_token || null;
        } catch {
            return null;
        }
    }

    init = () => {
        // Was constants.storage.jwt, which does not exist on constants.storage
        // (the keys are idToken / accessToken / refreshToken / expiry / user /
        // loggedIn / agreeCookies). getItem(undefined) returns null, so the
        // guard below never fired and the Authorization header was never sent
        // on any HTTP call — the only credential in play was the hardcoded
        // X-Api-Key.
        this.api_token = localStorage.getItem(constants.storage.accessToken);

        let headers = {
            'Content-Type': 'application/json',
            'X-Api-Key': this.api_key,
        };

        if (this.api_token) {
            headers.Authorization = `Bearer ${this.api_token}`;
        }

        this.client = axios.create({
            baseURL: this.api_url,
            timeout: 55000, // 55 seconds to allow for API latency
            headers: headers,
        });

        return this.client;
    };


    // Smart method that chooses the right API based on config
    MessagesCreate = async (data, countryContext, specificCountry = null, onChunk = null) => {
        // Checked first so it overrides the Kendra path. Flip
        // features.useHttpStreaming to false to fall straight back to the
        // WebSocket without redeploying anything.
        if (this.useHttpStreaming) {
            // No guard on agent_api_url: empty is valid and means same-origin.
            console.log('🌊 Using AgentCore front door (SSE)', this.agent_api_url || '(same origin)');
            return this.MessagesCreateAgent(data, countryContext, specificCountry, onChunk);
        }

        if (this.useKendraAPI) {
            console.log('🔍 Using Kendra API');

            try {
                // Pass full data to Kendra handler - it will extract what's needed
                const response = await this.MessagesCreateKendra(data, countryContext, specificCountry, onChunk);

                console.log('🔍 Raw Kendra response:', response);
                console.log('🔍 Response data:', response.data);
                console.log('🔍 Response status:', response.status);

                // Transform Kendra response format to match expected format
                // Kendra returns { message: "..." } but ChatComponent expects { summary: "..." }
                if (response.data && response.data.message) {
                    response.data.summary = response.data.message;
                    console.log('🔍 Added summary field:', response.data.summary);
                } else {
                    console.log('🔍 No message field found in response.data');
                }

                console.log('🔍 Final transformed response:', response.data);
                return response;
            } catch (error) {
                console.error('🔍 Kendra API error:', error);
                console.error('🔍 Error details:', error.response?.data);
                throw error;
            }
        } else {
            console.log('📦 Using Product API');
            return this.MessagesCreateClaude(data, countryContext, specificCountry);
        }
    }

    // Legacy method for /messages endpoint (keeping for backward compatibility)
    MessagesCreateLegacy = (data, countryContext) => {
        return this.init().post(`/messages/${countryContext}`, data);
    }

    MessagesCreateClaude = (data, countryContext) => {
        // Transform data for Product API - it expects 'text' field
        const verbosityInstruction = data.verbosity_instruction || '';
        const queryText = data.current_query || data.text || '';
        const finalText = verbosityInstruction ? `${queryText} ${verbosityInstruction}` : queryText;
        const productApiData = {
            ...data,
            text: finalText
        };
        return this.init().post(`/ask_product?region=${countryContext}`, productApiData);
    }

    // Extract user query from request (AWS Best Practice: direct query)
    extractUserQuery = (data) => {
        // New format: direct current_query
        if (data.current_query) {
            return data.current_query;
        }

        // Legacy format: extract from conversation history
        if (data.text && Array.isArray(data.text)) {
            for (let i = data.text.length - 1; i >= 0; i--) {
                const message = data.text[i];
                if (message.role === 'user') {
                    return message.content;
                }
            }
        }

        // Fallback
        return data.query || '';
    }

    // Generate user-based persistent session ID following AWS best practices
    generateUserSessionId = (userEmail) => {
        // Create persistent session per user (resets daily for fresh context)
        const dateString = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
        const userHash = userEmail ? btoa(userEmail).replace(/[^a-zA-Z0-9]/g, '').substring(0, 10) : 'anonymous';
        return `user_${userHash}_${dateString}`;
    }

    // Get context suffix for country
    getContextSuffix = (countryContext, specificCountry = null) => {
        // If specific country is provided, use it
        if (specificCountry) {
            return `for the ${specificCountry} market`;
        }

        // Fallback to region-based context
        const countryContextMap = {
            'usa': 'for the United States market',
            'uk': 'for the United Kingdom and Ireland market',
            'br': 'for the Brazil market',
            'eu': 'for the UK and European Union market',
            'apac': 'for the APAC market'
        };
        return countryContextMap[countryContext] || `for the ${countryContext} market`;
    }


    MessagesCreateKendra = (data, countryContext, specificCountry = null, onChunk = null) => {
        // Extract current user query directly
        const userQuery = this.extractUserQuery(data);

        // Get verbosity instruction if provided
        const verbosityInstruction = data.verbosity_instruction || '';

        // Get persisted session ID or let server create one
        const persistedSessionId = localStorage.getItem('kendra_session_id');

        // Add country context to query, with verbosity instruction at the END so the model follows it
        const contextSuffix = this.getContextSuffix(countryContext, specificCountry);
        const finalQuery = verbosityInstruction 
            ? `${userQuery} ${contextSuffix} respond in markdown format. ${verbosityInstruction}`
            : `${userQuery} ${contextSuffix} respond in markdown format`;

        // WebSocket request format - include session_id only if we have one
        const bedrockRequest = {
            action: 'ask_kendra',
            query: finalQuery
        };

        if (persistedSessionId) {
            bedrockRequest.session_id = persistedSessionId;
        }

        console.log('🔌 Kendra WebSocket API Debug:');
        console.log('  User Query:', userQuery);
        console.log('  Final Query:', bedrockRequest.query);
        console.log('  Existing Session ID:', persistedSessionId || 'None - server will create');
        console.log('  WebSocket URL:', this.api_ws_url);
        console.log('  Request:', JSON.stringify(bedrockRequest, null, 2));

        return this.sendWebSocketMessage(bedrockRequest, onChunk);
    }

    CheckoutSessionCreate = (data) => {
        return this.init().post(`/checkout/`);
    }

    sendWebSocketMessage = (message, onChunk = null) => {
        return new Promise((resolve, reject) => {
            try {
                const ws = new WebSocket(this.api_ws_url);
                let responseData = '';
                let isComplete = false;

                // Exposed so cancelActiveRequest can close it mid-answer.
                this.activeSocket = ws;

                ws.onopen = () => {
                    console.log('🔌 WebSocket connected to:', this.api_ws_url);
                    ws.send(JSON.stringify(message));
                };

                ws.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);
                        console.log('📨 WebSocket message received:', data);

                        // Persist session_id if provided by server
                        if (data.session_id) {
                            localStorage.setItem('kendra_session_id', data.session_id);
                            console.log('💾 Session ID persisted:', data.session_id);
                        }

                        if (data.type === 'stream_chunk') {
                            // Handle streaming chunks (your server uses 'stream_chunk')
                            const chunk = data.chunk || '';
                            responseData += chunk;
                            console.log('📝 Accumulated response length:', responseData.length);
                            
                            // Call the streaming callback if provided
                            if (onChunk && chunk) {
                                onChunk(chunk, responseData);
                            }
                        } else if (data.type === 'chunk') {
                            // Handle alternative chunk format
                            const chunk = data.content || '';
                            responseData += chunk;
                            
                            // Call the streaming callback if provided
                            if (onChunk && chunk) {
                                onChunk(chunk, responseData);
                            }
                        } else if (data.type === 'response') {
                            // Final response (your server uses 'response')
                            isComplete = true;
                            const finalMessage = responseData || data.message;
                            console.log('✅ Complete response received, length:', finalMessage.length);
                            ws.close();
                            resolve({
                                data: {
                                    message: finalMessage,
                                    summary: finalMessage,
                                    session_id: data.session_id
                                },
                                status: 200
                            });
                        } else if (data.type === 'complete') {
                            // Alternative final response format
                            isComplete = true;
                            ws.close();
                            resolve({
                                data: {
                                    message: responseData || data.message,
                                    summary: responseData || data.message,
                                    session_id: data.session_id
                                },
                                status: 200
                            });
                        } else if (data.type === 'error') {
                            reject(new Error(data.message || 'WebSocket error'));
                        } else if (data.type === 'processing' || data.type === 'stream_start') {
                            // Ignore processing/status messages
                            console.log('ℹ️ Status message:', data.message);
                        } else {
                            // Handle unknown message types
                            console.log('❓ Unknown message type:', data.type, data);
                        }
                    } catch (parseError) {
                        console.error('Error parsing WebSocket message:', parseError);
                        // Treat as plain text chunk
                        responseData += event.data;
                    }
                };

                ws.onerror = (error) => {
                    console.error('🔌 WebSocket error:', error);
                    reject(new Error('WebSocket connection error'));
                };

                ws.onclose = (event) => {
                    console.log('🔌 WebSocket closed:', event.code, event.reason);
                    if (this.activeSocket === ws) {
                        this.activeSocket = null;
                    }
                    if (!isComplete) {
                        if (responseData) {
                            // Return partial response if we have data
                            resolve({
                                data: {
                                    message: responseData,
                                    summary: responseData
                                },
                                status: 200
                            });
                        } else {
                            reject(new Error(`WebSocket closed without complete response: ${event.code} ${event.reason}`));
                        }
                    }
                };

                // Set a timeout for the WebSocket connection (no timeout limit like HTTP)
                setTimeout(() => {
                    if (ws.readyState === WebSocket.CONNECTING || ws.readyState === WebSocket.OPEN) {
                        ws.close();
                        reject(new Error('WebSocket timeout after 120 seconds'));
                    }
                }, 120000); // 2 minute timeout - much longer than HTTP

            } catch (error) {
                console.error('🔌 WebSocket setup error:', error);
                reject(error);
            }
        });
    }

    /* ================================================================== *
     * AgentCore front door (HTTPS + SSE)
     *
     * Replacement for the WebSocket path above. Lives alongside it rather
     * than over it, so the two can be switched with the
     * features.useHttpStreaming flag and rolled back without a redeploy.
     *
     * Why the transport changed at all: a browser cannot set headers on a
     * WebSocket. The constructor takes a URL and an optional subprotocol
     * array, and nothing else. That means a bearer token has to travel in the
     * query string, where it lands in access logs and browser history — which
     * is almost certainly why the $connect authorizer was commented out
     * rather than finished. Over HTTPS the credential is a cookie or a header,
     * and it is checked on every request instead of once at connect.
     * ================================================================== */

    MessagesCreateAgent = async (data, countryContext, specificCountry = null, onChunk = null) => {
        const question = this.extractUserQuery(data);

        // Structured fields, not a pre-baked prompt string. The old path
        // concatenated the verbosity instruction and market context onto the
        // user's text client-side, which made the entire prompt
        // client-controlled. The server now composes it from closed maps.
        // Region and country are sent as SEPARATE fields, and region is always
        // sent.
        //
        // This previously read `region: specificCountry ? null : countryContext`,
        // mirroring getContextSuffix below, where a specific country produced
        // its own suffix and so replaced the region. That precedence does not
        // survive here, because this body has nowhere to put the country: the
        // result was that region went out as null while the country went
        // nowhere at all. selectedCountry defaults to "United States" and is
        // never cleared, so the condition was always true and the market
        // selector had no effect on any answer.
        //
        // The server holds the precedence now (country wins when both are
        // present) and validates each against a closed map, so neither field
        // can carry an instruction.
        const body = {
            question,
            region: countryContext || null,
            country: specificCountry || null,
            verbosity: this.mapVerbosity(data.verbosity_instruction, data.verbosity_level),
            thread_id: localStorage.getItem(constants.localStorage.threadId) || null,
        };

        return this.sendHttpStreamingMessage(body, onChunk);
    }

    /**
     * The component works in prose instructions; the front door takes a closed
     * enum, so that the client cannot dictate arbitrary instructions.
     *
     * Prefer the numeric level when the caller supplies it. Inferring from the
     * prose is genuinely ambiguous: the middle setting also contains the word
     * "concise", so keyword matching reads it as the shortest one. The prose
     * path is kept only as a fallback for callers that predate verbosity_level.
     */
    mapVerbosity = (instruction, level) => {
        const byLevel = { 0: 'brief', 1: 'standard', 2: 'detailed' };
        if (level !== undefined && level !== null && byLevel[level]) {
            return byLevel[level];
        }

        if (!instruction) return null;
        const text = String(instruction).toLowerCase();
        // Check the "long" signals first: they are unambiguous, whereas
        // "concise" appears in more than one level.
        if (text.includes('detail') || text.includes('comprehensive') || text.includes('thorough')) return 'detailed';
        if (text.includes('2-3 sentences') || text.includes('very brief')) return 'brief';
        return 'standard';
    }

    /**
     * POST /api/ask and consume the SSE stream.
     *
     * Resolves with the same shape sendWebSocketMessage resolves with, and
     * calls onChunk(chunk, accumulated) identically, so ChatComponent needs no
     * changes at all.
     */
    sendHttpStreamingMessage = async (body, onChunk = null) => {
        const controller = new AbortController();
        // The WebSocket path had a 120s timer it never cleared and no way to
        // cancel an in-flight question. This one is abortable.
        const timeout = setTimeout(() => controller.abort(), 300000);

        // Exposed so cancelActiveRequest can abort it mid-answer.
        this.activeAbort = controller;

        let accumulated = '';

        try {
            const headers = { 'Content-Type': 'application/json' };

            // X-Auth-Token, NOT Authorization. CloudFront's origin access
            // control uses the Authorization header for its own SigV4
            // signature; forwarding a viewer Authorization header overwrites
            // it and the Lambda Function URL rejects the request with a 403
            // before the function is even invoked. So the distribution's
            // origin request policy forwards X-Auth-Token and drops
            // Authorization.
            //
            // Only needed until BFF is wired up, at which point the httpOnly
            // cookie carries the session and this returns null.
            const bearer = this.getBearerToken();
            if (bearer) headers['X-Auth-Token'] = bearer;

            // Serialise once: the payload hash must cover the exact bytes sent.
            const bodyText = JSON.stringify(body);
            headers['x-amz-content-sha256'] = await this.sha256Hex(bodyText);

            const response = await fetch(`${this.agent_api_url}/api/ask`, {
                method: 'POST',
                headers,
                // Sends the httpOnly session cookie once BFF is wired up. Safe
                // on a same-origin request, and required if agentApi is ever
                // pointed at a different origin.
                credentials: 'include',
                body: bodyText,
                signal: controller.signal,
            });

            if (!response.ok) {
                throw new Error(await this.describeHttpError(response));
            }
            if (!response.body) {
                throw new Error('Streaming is not supported by this browser');
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let finalMessage = null;
            let sessionId = null;

            while (true) {
                const { value, done } = await reader.read();
                if (done) break;

                // stream: true so a multi-byte character split across two
                // network chunks is not mangled.
                buffer += decoder.decode(value, { stream: true });

                // THE IMPORTANT BIT. WebSocket frames are message-atomic, so
                // the old code could JSON.parse every onmessage payload. HTTP
                // reads are not: one read can contain half an event, or three
                // and a bit. Only complete frames (terminated by a blank line)
                // are parsed; the remainder stays buffered.
                const frames = buffer.split('\n\n');
                buffer = frames.pop() ?? '';

                for (const frame of frames) {
                    const payload = this.parseSseFrame(frame);
                    if (payload === null) continue;      // comment or keepalive
                    if (payload === '[DONE]') continue;  // terminator

                    if (payload.session_id) {
                        sessionId = payload.session_id;
                        localStorage.setItem(constants.localStorage.threadId, sessionId);
                    }

                    switch (payload.type) {
                        case 'stream_chunk': {
                            const chunk = payload.chunk || '';
                            if (!chunk) break;
                            accumulated += chunk;
                            if (onChunk) onChunk(chunk, accumulated);
                            break;
                        }
                        case 'response':
                            finalMessage = payload.message || accumulated;
                            break;
                        case 'error':
                            throw new Error(payload.message || 'The agent returned an error');
                        case 'warning':
                            // Truncation and iteration caps arrive here. Worth
                            // seeing rather than silently accepting a clipped
                            // answer as complete.
                            console.warn('⚠️ Agent warning:', payload.stopReason);
                            break;
                        case 'tool_use':
                            console.log('🔎 Retrieval:', payload.tool);
                            break;
                        case 'processing':
                        case 'stream_start':
                            break;
                        default:
                            console.log('❓ Unhandled event type:', payload.type, payload);
                    }
                }
            }

            const message = finalMessage ?? accumulated;
            if (!message) {
                throw new Error('The agent returned an empty response');
            }

            return {
                data: { message, summary: message, session_id: sessionId },
                status: 200,
            };
        } catch (error) {
            if (error.name === 'AbortError') {
                // A partial answer is more useful than an error, matching the
                // old onclose behaviour.
                if (accumulated) {
                    return {
                        data: { message: accumulated, summary: accumulated, partial: true },
                        status: 200,
                    };
                }
                throw new Error('The request timed out');
            }
            console.error('🌊 Streaming request failed:', error);
            throw error;
        } finally {
            clearTimeout(timeout);
            if (this.activeAbort === controller) {
                this.activeAbort = null;
            }
        }
    }

    /**
     * Hex-encoded SHA-256 of the request body.
     *
     * Required, not optional. CloudFront's origin access control signs requests
     * to the Lambda function URL with SigV4, and per the CloudFront docs:
     *
     *   "If you use PUT or POST methods with your Lambda function URL, your
     *    users must compute the SHA256 of the body and include the payload hash
     *    value of the request body in the x-amz-content-sha256 header when
     *    sending the request to CloudFront. Lambda doesn't support unsigned
     *    payloads."
     *
     * Omit it and every POST fails with InvalidSignatureException before the
     * Lambda is invoked. The hash MUST be of the exact body string that is
     * sent, so both come from the same variable.
     *
     * crypto.subtle needs a secure context, which CloudFront always is.
     */
    sha256Hex = async (text) => {
        const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
        return Array.from(new Uint8Array(digest))
            .map((b) => b.toString(16).padStart(2, '0'))
            .join('');
    }

    /** Returns the parsed data payload, the string '[DONE]', or null. */
    parseSseFrame = (frame) => {
        const dataLines = [];
        for (const line of frame.split('\n')) {
            if (!line || line.startsWith(':')) continue; // comment / keepalive
            if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
        }
        if (dataLines.length === 0) return null;

        const raw = dataLines.join('\n');
        if (raw === '[DONE]') return '[DONE]';

        try {
            return JSON.parse(raw);
        } catch {
            // Deliberately not appended to the answer. The old code's parse
            // fallback pushed unparseable text straight into responseData and
            // never called onChunk, so corruption was invisible.
            console.warn('Discarding unparseable SSE frame:', raw.slice(0, 120));
            return null;
        }
    }

    /**
     * GET /api/history — the caller's recent questions.
     *
     * No user id in the request. The server scopes the query to the subject on
     * the verified token, so there is no parameter here that could be pointed
     * at somebody else's history.
     *
     * No payload hash header: that is only required for PUT and POST through
     * CloudFront's origin access control, because Lambda function URLs reject
     * unsigned payloads. A GET has no body to hash.
     */
    getHistory = async () => {
        const headers = { Accept: 'application/json' };
        const bearer = this.getBearerToken();
        if (bearer) headers['X-Auth-Token'] = bearer;

        const response = await fetch(`${this.agent_api_url}/api/history`, {
            method: 'GET',
            headers,
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error(await this.describeHttpError(response));
        }

        const body = await response.json();
        return Array.isArray(body?.threads) ? body.threads : [];
    }

    /**
     * GET /api/history/{threadId} — one saved conversation.
     *
     * Reconstructed server-side from AgentCore managed memory, which already
     * holds every turn, so the browser is not the system of record for a
     * transcript and a conversation opens on any device.
     *
     * A 404 here means "not yours or not found", which the server cannot and
     * should not distinguish.
     */
    getThreadMessages = async (threadId) => {
        if (!threadId) throw new Error('A thread id is required');

        const headers = { Accept: 'application/json' };
        const bearer = this.getBearerToken();
        if (bearer) headers['X-Auth-Token'] = bearer;

        const response = await fetch(
            `${this.agent_api_url}/api/history/${encodeURIComponent(threadId)}`,
            { method: 'GET', headers, credentials: 'include' }
        );

        if (!response.ok) {
            throw new Error(await this.describeHttpError(response));
        }

        const body = await response.json();
        return {
            threadId: body?.thread_id ?? threadId,
            title: body?.title ?? null,
            messages: Array.isArray(body?.messages) ? body.messages : [],
        };
    }

    describeHttpError = async (response) => {
        if (response.status === 401) return 'Your session has expired. Please sign in again.';
        if (response.status === 403) return 'This request was refused. Please refresh and try again.';
        if (response.status === 429) return 'Too many requests. Please wait a moment and try again.';
        try {
            const body = await response.json();
            if (body?.error) return body.error;
        } catch {
            /* fall through to the generic message */
        }
        return `Request failed (${response.status})`;
    }

}
